import { SecondaryButton } from 'src/components/design-system/form/buttons/SecondaryButton';
import { PrimaryButton } from 'src/components/design-system/form/buttons/PrimaryButton';
import { ApplicationModal } from 'src/components/Modal/LegacyModal';
import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import { notifyAboutException } from 'src/utils/notify';
import AvatarEditor from 'react-avatar-editor';
import { Avatar } from 'src/components/Avatar';
import { ResponseError } from 'src/api';
import {
    useGetProfileImageQuery,
    useResetProfileImageMutation,
    useUploadProfileImageMutation,
} from 'src/api/endpoints/profile';
import './module.scss';
import { Skeleton } from 'antd';

export const AvatarUploadWidget: FC = (): ReactElement => {
    const [resetProfileImage, { isLoading: isProfileImageReset }] =
        useResetProfileImageMutation();

    const [uploadProfileImage, { isLoading: isProfileImageUploading }] =
        useUploadProfileImageMutation();

    const { data: profileImage, isFetching: isProfileImageFetching } =
        useGetProfileImageQuery();

    const [isProfileImagePreparing, setProfileImagePreparing] = useState(false);

    const onResetProfileImage = () => {
        if (!profileImage) return;

        resetProfileImage()
            .unwrap()
            .catch((reason: ResponseError) => {
                notifyAboutException({ content: reason.message });
            });
    };

    const fileInput = useRef<HTMLInputElement>(null);
    const editorRef = useRef<AvatarEditor>(null);

    const uploadedProfileImage = useRef<File | null>(null);

    const [isProfileImageEditorOpen, setProfileImageEditorOpen] =
        useState(false);

    const [scale, setScale] = useState(1);

    const [isEditorImageLoading, setEditorImageLoading] = useState(false);

    const closeProfileImageEditor = () => {
        setProfileImageEditorOpen(false);
    };

    const openProfileImageEditor = () => {
        setScale(1);
        setProfileImageEditorOpen(true);
    };

    const onUpdateProfileImage = (
        originalImage: File,
        editedImage: HTMLCanvasElement
    ) => {
        setProfileImagePreparing(true);

        editedImage.toBlob((blob): void => {
            if (!blob) return;

            const file = new File([blob], originalImage.name, {
                type: 'image/jpeg',
            });

            const fileSize: number = Math.floor(file.size / 1024 ** 2);
            setProfileImagePreparing(false);

            if (fileSize >= 3)
                return notifyAboutException({
                    content: 'Вес изображения слишком велик',
                });

            uploadProfileImage({ payload: { file } })
                .unwrap()
                .then(closeProfileImageEditor)
                .catch((reason: ResponseError) => {
                    notifyAboutException({
                        sound: true,
                        content: reason.message,
                    });
                });
        }, 'image/jpeg');
    };

    return (
        <>
            <ApplicationModal
                isOpen={isProfileImageEditorOpen}
                onClose={closeProfileImageEditor}
                header={'Редактирование фотографии профиля'}
            >
                {uploadedProfileImage.current && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'start',
                            alignItems: 'center',

                            flexDirection: 'column',
                        }}
                    >
                        <div className="avatar-editor">
                            <div
                                style={{
                                    position: 'relative',
                                }}
                            >
                                <ImageEditorSkeleton
                                    active={isEditorImageLoading}
                                    size={430}
                                />
                                <AvatarEditor
                                    ref={editorRef}
                                    border={0}
                                    width={430}
                                    height={430}
                                    backgroundColor={'white'}
                                    scale={scale}
                                    image={uploadedProfileImage.current}
                                    onImageReady={() =>
                                        setEditorImageLoading(false)
                                    }
                                    borderRadius={215}
                                />
                            </div>
                            <div className="controls">
                                <SecondaryButton
                                    className={['scale-button']}
                                    label={'+'}
                                    disabled={
                                        scale >= 5 ||
                                        isEditorImageLoading ||
                                        isProfileImagePreparing ||
                                        isProfileImageUploading
                                    }
                                    onClick={() =>
                                        setScale(p => Math.min(p + 0.5, 5))
                                    }
                                />
                                <SecondaryButton
                                    className={['scale-button']}
                                    label={'-'}
                                    disabled={
                                        scale <= 1 ||
                                        isEditorImageLoading ||
                                        isProfileImagePreparing ||
                                        isProfileImageUploading
                                    }
                                    onClick={() =>
                                        setScale(p => Math.max(p - 0.5, 1))
                                    }
                                />
                            </div>
                        </div>
                        <div className="modal-buttons">
                            <SecondaryButton
                                label={'Отменить'}
                                onClick={closeProfileImageEditor}
                            />

                            <PrimaryButton
                                label={'Загрузить'}
                                loading={
                                    isProfileImagePreparing ||
                                    isProfileImageUploading
                                }
                                disabled={isEditorImageLoading}
                                onClick={() => {
                                    if (!uploadedProfileImage.current) return;
                                    if (!editorRef.current) return;

                                    onUpdateProfileImage(
                                        uploadedProfileImage.current,
                                        editorRef.current.getImage()
                                    );
                                }}
                            />
                        </div>
                    </div>
                )}
            </ApplicationModal>
            <div className="avatar-field">
                <Avatar
                    width={96}
                    height={96}
                    src={`data:image/png;base64, ${profileImage?.base}`}
                    isLoading={
                        isProfileImageFetching || profileImage === undefined
                    }
                />
                <div className="avatar-field-controls">
                    Изображение профиля
                    <div className="avatar-actions">
                        <input
                            type={'file'}
                            ref={fileInput}
                            accept="image/png, image/jpeg"
                            style={{
                                visibility: 'hidden',
                                width: '0',
                                height: '0',
                                display: 'none',
                            }}
                            onInput={event => {
                                const files: FileList | null =
                                    event.currentTarget.files;

                                if (!files) return;

                                const newProfileImage: File | null =
                                    files.item(0);

                                if (!newProfileImage) return;

                                uploadedProfileImage.current = newProfileImage;

                                event.currentTarget.value = '';
                                event.currentTarget.files = null;

                                setEditorImageLoading(true);
                                openProfileImageEditor();
                            }}
                        />

                        <PrimaryButton
                            label={'Загрузить'}
                            disabled={isProfileImageReset}
                            onClick={() => fileInput.current?.click?.()}
                        />

                        {profileImage && !isProfileImageFetching && (
                            <SecondaryButton
                                label={'Сбросить'}
                                loading={isProfileImageReset}
                                onClick={onResetProfileImage}
                            />
                        )}
                    </div>
                    <div className="avatar-upload-text">
                        Мы поддерживаем форматы изображений PNG и JPEG.
                        Максимально допустимый размер изображения 3МБ
                    </div>
                </div>
            </div>
        </>
    );
};

export const ImageEditorSkeleton = (props: {
    size: number;
    active: boolean;
}) => {
    const [isDisplaying, setDisplaying] = useState(false);

    useEffect(() => {
        if (props.active) return setDisplaying(true);

        const timeout = setTimeout(() => {
            if (!props.active) setDisplaying(false);
        }, 220);

        return () => clearInterval(timeout);
    }, [props.active]);

    return (
        <div
            style={{
                position: 'absolute',
                zIndex: 2,
                opacity: Number(props.active),
                transition: 'opacity 0.2s ease-in-out',
                width: props.size,
                height: props.size,
                borderRadius: '10px',
                display: isDisplaying ? 'flex' : 'none',
                justifyContent: 'center',
                alignItems: 'center',
                background: '#a4a4a4',
            }}
        >
            <div
                style={{
                    width: props.size,
                    height: props.size,
                    background: '#EDF1F7',
                    borderRadius: '50%',
                }}
            >
                <Skeleton.Avatar
                    style={{
                        width: props.size,
                        height: props.size,
                    }}
                    active={true}
                />
            </div>
        </div>
    );
};
